<template>
  <!-- edit profile dialog -->
  <div>
    <v-dialog
      v-model="isCountryAttributesDialogOpen"
      max-width="1200px"
      @click:outside="$emit('update:is-country-attributes-dialog-open',false)" 
      v-if="projectDataLocal.countries"
    >
      <v-card 
        class="project-edit-info pa-sm-10 pa-3"
        :style="`border: 2px solid var(--v-${resolveCountryStatusVariant(projectDataLocal.countries[index].status)}-base !important;`"
      >
        <!-- Alert -->
        <alert />
        <v-card-title class="justify-center text-h5">
          Edit {{projectData.countries[index].country_detail.name}} Information
        </v-card-title>
        <v-card-text class="text-center mt-n2">
          Updating {{projectData.countries[index].country_detail.name}} details will send notifications to appropriate parties.
        </v-card-text>
        
        <v-card-text class="mt-5">
          
          <v-form 
            ref="countryForm"
            class="multi-col-validation"
          >
          

            <v-card class="mb-7" flat>
              <v-card-title>
                Project Elements
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col 
                    cols="12"
                    md="11">
                    <v-autocomplete
                      v-model="projectDataLocal.countries[index].country"
                      :loading="countryLoading"
                      :search-input.sync="countrySearch"
                      :items="filteredCountries"
                      item-text="label"
                      item-value="value"
                      label="Country"
                      persistent-hint
                      outlined
                      dense
                      placeholder="Country"
                      @change="countrySearch=''"
                      :rules="[validators.required]"
                      :disabled="disableCountryChange"
                    ></v-autocomplete>
                  </v-col>
                  <v-col
                    cols="12"
                    md="1">
                    <v-btn
                      outlined
                      icon
                      @click="disableCountryChange = !disableCountryChange"
                    >
                      <v-icon>{{ icons.mdiPencilOutline }}</v-icon>
                    </v-btn>
                  </v-col>
                  </v-row>
                  <v-row>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-autocomplete
                      v-model="projectDataLocal.countries[index].status"
                      :loading="statusLoading"
                      :items="statusOptions"
                      item-text="label"
                      item-value="value"
                      label="Status"
                      outlined
                      dense
                      hide-details="auto"
                      placeholder="Status"
                      :rules="[validators.required]"
                      :color="resolveCountryStatusVariant(projectDataLocal.countries[index].status)"
                      @change="checkStatus"
                    >
                      <template v-slot:selection="data">
                        <v-chip
                          v-bind="data.attrs"
                          :input-value="data.selected"
                          small
                          label
                          :color="resolveCountryStatusVariant(projectDataLocal.countries[index].status)"
                          :class="`v-chip-light-bg ${resolveCountryStatusVariant(projectDataLocal.countries[index].status)}--text font-weight-medium text-capitalize ml-2`"
                        >
                          {{ resolveCountryStatusLabelVariant(projectDataLocal.countries[index].status) }}
                        </v-chip>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col 
                    cols="12"
                    md="6">
                    <v-autocomplete
                      v-model="projectDataLocal.countries[index].modules"
                      :loading="moduleLoading"
                      :items="moduleOptions"
                      item-text="label"
                      item-value="value"
                      label="Modules"
                      hint="Modules?! Make your selection(s)"
                      persistent-hint
                      outlined
                      dense
                      multiple
                      small-chips
                      deletable-chips
                      hide-details="auto"
                      placeholder="Modules"
                      :rules="[validators.required]"
                    ></v-autocomplete>
                  </v-col>
                  <v-col 
                    cols="12"
                    md="6">
                    <v-autocomplete
                      v-model="projectDataLocal.countries[index].shelf_type"
                      :loading="shelfTypeLoading"
                      :items="shelfTypeOptions"
                      item-text="label"
                      item-value="value"
                      label="Shelf Type"
                      hint="2D Shelf = online or need of posters. 3D Shelf or No Shelf = Studio can relax!"
                      persistent-hint
                      outlined
                      dense
                      hide-details="auto"
                      placeholder="Shelf Type"
                      :rules="[validators.required]"
                    ></v-autocomplete>
                  </v-col>
                  <v-col 
                    cols="12"
                    md="6">
                    <v-autocomplete
                      v-model="projectDataLocal.countries[index].studio_rate_card"
                      :loading="studioRateCardLoading"
                      :items="studioRateCardOptions"
                      item-text="label"
                      item-value="value"
                      label="Studio Rate Card"
                      hint="Option 1 = Cheap,..., Option 6 = Expensive"
                      persistent-hint
                      outlined
                      dense
                      hide-details="auto"
                      placeholder="Studio Rate Card"
                      :rules="[validators.required]"
                    ></v-autocomplete>
                  </v-col>
                  <v-col 
                    cols="12"
                    md="6"
                  >
                    <v-text-field
                      v-model="projectDataLocal.countries[index].sample"
                      label="Sample Size"
                      type="number"
                      outlined
                      dense
                      hide-details="auto"
                      placeholder="Sample Size"
                      :rules="[validators.required, validators.integerValidator]"
                    ></v-text-field>
                  </v-col>
                  <v-col 
                    cols="12"
                    md="6">
                    <v-text-field
                      v-model="projectDataLocal.countries[index].phase"
                      label="Phases"
                      type="number"
                      outlined
                      dense
                      hide-details="auto"
                      placeholder="Phases"
                      hint="Number of cell(s)"
                      persistent-hint
                      :rules="[validators.required, validators.integerValidator]"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    md="6"
                    v-if="projectDataLocal.project_type == 2">
                    <v-autocomplete
                      v-model="projectDataLocal.countries[index].qual_sample_type"
                      :loading="qualSampleLoading"
                      :items="qualSampleOptions"
                      item-text="label"
                      item-value="value"
                      label="Qual Sample Type"
                      hint="Select your Qualitative Sample Type(s)"
                      persistent-hint
                      outlined
                      dense
                      multiple
                      small-chips
                      deletable-chips
                      hide-details="auto"
                      placeholder="Qual Sample Type"
                      :rules="[validators.required]"
                    ></v-autocomplete>
                  </v-col>
                  <v-col
                    v-for="dept in DEPARTMENTS_MAP"
                    :key="dept.key"
                    cols="12" md="6"
                    v-if="checkIfDeptSelected(dept.deptName)"
                  >
                    <v-autocomplete
                      v-model="projectDataLocal.countries[index][dept.key]"
                      :items="usersByDept[dept.key]"
                      :loading="userLoading"
                      :label="dept.label"
                      multiple outlined dense small-chips deletable-chips
                      item-text="name" item-value="id"
                    >
                      <template #item="{ item }">
                        <div class="d-flex align-center">
                          <v-avatar :color="stringToHslColor(item.name)" size="25" class="me-2">
                            <span class="white--text text-xs">{{ avatarText(item.name) }}</span>
                          </v-avatar>
                          <span class="text-sm">{{ item.name }}</span>
                        </div>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  
                </v-row>
              </v-card-text>
            </v-card>
            <field-location-add
              ref="fieldLocations"
              :title="`Field Locations`"
              :isCountryAttributesDialogOpen="isCountryAttributesDialogOpen"
              :fieldCountry="projectDataLocal.countries[index]"
              :projectType="projectDataLocal.project_type"
              :fieldLocations="projectDataLocal.countries[index].field_locations"
              v-if="[1, 2, 5, 6].includes(projectData.countries[index].research_location)"
            >
            </field-location-add>
          
            <v-row>
              <v-col
                    cols="12"
                    class="d-flex justify-center mt-3"
                  >
                    <v-btn
                      color="primary"
                      class="me-3"
                      type="submit"
                      :loading="updateLoading"
                      :disabled="updateLoading"
                      @click.prevent="onSubmit"
                    >
                      Update
                    </v-btn>

                    <v-btn
                      outlined
                      color="secondary"
                      @click.prevent="$emit('update:is-country-attributes-dialog-open',false)"
                    >
                      Cancel
                    </v-btn>
                  </v-col>
            </v-row>
            
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="isDialogNoPOShow"
      persistent
      max-width="290px"
    >
      <v-card>
        <v-card-title class="text-h5">
          Warning! No POs
        </v-card-title>
        <v-card-text>No Purchase Orders have been generated for this project. Please ensure they are created.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary darken-1"
            text
            @click="isDialogNoPOShow = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <v-dialog
      v-model="isDialogStatusShow"
      persistent
      max-width="500px"
    >
      <v-card>
        <v-card-title class="headline">
          Status Confirmation
        </v-card-title>

        <div v-if="localCsatResult.includes('2')">
        <v-card-text class="text-lg pb-0">Please confirm setting the project status to Completed</v-card-text>
        <v-card-text class="text-xs mt-0">Note: By setting project to Completed, the project folder will be archived, Teams Channel and Planner are going to be deleted!</v-card-text>
        </div>
        <v-card-text v-else>Please fill out the feedback loop before setting the project status to Completed</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
        <v-btn
          color="primary"
          :disabled="localCsatResult.length === 0 || !localCsatResult.includes('2')"
          @click="isDialogStatusShow = false"
        >
          Confirm
        </v-btn>
        <v-btn
          color="error"
          outlined
          @click="isDialogStatusShow = false; projectDataLocal.countries[index].status = projectData.countries[index].status"
        >
          Discard
        </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, watch, computed, onMounted } from '@vue/composition-api'
import useProject from '../../useProject'
import useProjectView from '../useProjectView'
import store from '@/store'
import Alert from '@/views/components/alert/Alert.vue'
import FieldLocationAdd from './FieldLocationAdd.vue'
import { stringToHslColor, formatOptions } from '@core/utils'
import { avatarText } from '@core/utils/filter'
import {
  required,
  requiredArray,
  integerValidator,
} from '@core/utils/validation'

import { 
  mdiCodeTags,
  mdiPlus,
  mdiClose,
  mdiPencilOutline
   } from '@mdi/js';

export default {
  components: {
    FieldLocationAdd,
    Alert
  },
  props: {
    isCountryAttributesDialogOpen: {
      type: Boolean,
      required: true,
    },
    index: {
      type: Number,
      required: true,
      default: () => 0
    },
    projectData: {
      type: Object,
      required: true,
    },
    csatResult: {
        type: Array,
        required: true
    }
  },

  setup(props, { emit }) {
    const isDialogStatusShow = ref(false)
    const isDialogNoPOShow = ref(false)
    const projectDataLocal = ref({})
    const disableCountryChange = ref(true)
    const departments = ref([])
    const user = ref({})
    const countryForm = ref(null)
    const fieldLocations = ref(null)
    const localCsatResult = ref(JSON.parse(JSON.stringify(props.csatResult)))

    user.value = JSON.parse(localStorage.getItem('user'))
    const DEPARTMENTS_MAP = [
      { key: 'field', label: 'Field', deptName: 'Field' },
      { key: 'studio', label: 'Studio', deptName: 'Studio' },
      { key: 'programming', label: 'Prgm/DP/Coding', deptName: 'Programming-DP-Coding' },
      { key: 'modeling', label: 'Modeling', deptName: 'Modeling' },
      { key: 'project_management', label: 'PM', deptName: 'Project Management' },
      { key: 'admin', label: 'Admin', deptName: 'Admin' },
      { key: 'logistic', label: 'Logistic', deptName: 'Logistic' },
    ]
    
    const {
      updateLoading,
      resolveCountryStatusVariant,
      resolveCountryStatusLabelVariant,
    } = useProjectView()

    

    const { 
      deptOptions,
      moduleLoading,
      moduleOptions,
      moduleSearch,
      userLoading,
      userOptions,
      userSearch,
      shelfTypeLoading,
      shelfTypeOptions,
      shelfTypeSearch,
      studioRateCardLoading,
      studioRateCardOptions,
      studioRateCardSearch,
      qualSampleLoading,
      qualSampleOptions,
      qualSampleSearch,
      researchLocLoading,
      researchLocOptions,
      researchLocSearch,
      statusLoading,
      statusOptions,
      statusSearch,
      countryLoading,
      countryOptions,
      countrySearch,
      

      fetchDepartments,
      fetchModules,
      fetchUsers,
      fetchShelfTypes,
      fetchStudioRateCards,
      fetchQualSampleType,
      fetchStatuses,
      fetchCountries

    } = useProject()


    onMounted(() => {
    })

    const onSubmit = () => {
      const isStepValid = countryForm.value.validate()
      // Let's revisit on field location requirements
      // const isStepValid = countryForm.value.validate() && (fieldLocations.value.locations.length == 0  ||
      //                     (fieldLocations.value ? fieldLocations.value.$children[0].validate() : true) &&
      //                     (fieldLocations.value ? fieldLocations.value.sumSample === 0 : true))
                          
      if (isStepValid) {
        const projectData = JSON.parse(JSON.stringify(projectDataLocal.value))
        updateLoading.value = true
        emit('update-project', projectData)
      }
    }

    const usersByDept = computed(() => {
      const map = {}
      DEPARTMENTS_MAP.forEach(dept => {
        const deptObj = deptOptions.value.find(d => d.label === dept.deptName)
        map[dept.key] = deptObj 
          ? userOptions.value.filter(u => u.department === deptObj.value) 
          : []
      })
      return map
    })

    const filteredCountries = computed(() => {
      // Filter out countries from project other than current country
      return countryOptions.value.filter(item => !projectDataLocal.value.countries.filter(country => {if(country.country != projectDataLocal.value.countries[props.index].country) return country}).map(c => c.country).includes(item.country))
    })

    const checkIfDeptSelected = computed(() => {
      return (deptLabel) => {
        if (deptLabel){
          return departments.value.filter(dept => dept.name === deptLabel).length > 0;
        }
        return false
      }
    })

    const checkStatus = (item) => {
      let sameStatus = true
      for (var i in projectDataLocal.value.countries){
        const country = projectDataLocal.value.countries[i]
        if (country.status !== 'COMP') {
          sameStatus = false
          break;
        }
      }
      
      if (props.projectData.po_items.length === 0) isDialogNoPOShow.value = true
      if(sameStatus){
        isDialogStatusShow.value = true
      }
    }

    

    watch(
        () => props.csatResult,
        () => {
          localCsatResult.value = JSON.parse(JSON.stringify(props.csatResult))
          console.log(props.csatResult)
        }
    )

    watch(
      () => props.projectData, 
      () => {
        updateLoading.value = false
        emit('update:is-country-attributes-dialog-open', false)
      }
    )
    watch(
      () => props.isCountryAttributesDialogOpen,
      (newVal, oldVal) => {

        if (newVal) {
          
          if (moduleOptions.value.length == 0) fetchModules()
          if (userOptions.value.length == 0) fetchUsers()
          if (shelfTypeOptions.value.length == 0) fetchShelfTypes()
          if (studioRateCardOptions.value.length == 0) fetchStudioRateCards()
          if (qualSampleOptions.value.length == 0) fetchQualSampleType()
          if (statusOptions.value.length == 0) fetchStatuses()
          if (deptOptions.value.length == 0) fetchDepartments()
          if (countryOptions.value.length == 0) fetchCountries()

        }

        updateLoading.value = false
        disableCountryChange.value = true
        departments.value = props.projectData.departments
        projectDataLocal.value = {
          id: props.projectData.id,
          job_number: props.projectData.job_number,
          project_type: props.projectData.project_type.id,
          closed_won_date: props.projectData.closed_won_date,
          report_date: props.projectData.report_date,
          countries: [
            
          ]
        }
        
        for (var idx in props.projectData.countries){
          let localCountry = props.projectData.countries[idx]
          projectDataLocal.value.countries.push(
            {
              modules: localCountry.modules,
              id: localCountry.id,
              phase: localCountry.phase,
              country: localCountry.country,
              sample: localCountry.sample,
              status: localCountry.status,
              qual_sample_type: localCountry.qual_sample_type.map(item => item.id),
              studio_rate_card: localCountry.studio_rate_card,
              shelf_type: localCountry.shelf_type,
              research_location: localCountry.research_location,
              field_locations: localCountry.field_locations,
              admin: localCountry.admin.map(item => item.id),
              field: localCountry.field.map(item => item.id),
              logistic: localCountry.logistic.map(item => item.id),
              modeling: localCountry.modeling.map(item => item.id),
              programming: localCountry.programming.map(item => item.id),
              project_management: localCountry.project_management.map(item => item.id),
              studio: localCountry.studio.map(item => item.id),
              field_start_date: localCountry.field_start_date,
              field_end_date: localCountry.field_end_date,
              created_by: localCountry.created_by,
              updated_by: user.value.id


            }
          )
        }
        
        store.commit('comp-alert/SET_MESSAGE', {})
      }
    )

    return {
      DEPARTMENTS_MAP,
      usersByDept,
      isDialogNoPOShow,
      isDialogStatusShow,
      projectDataLocal,
      moduleLoading,
      moduleOptions,
      moduleSearch,
      userLoading,
      userOptions,
      userSearch,
      shelfTypeLoading,
      shelfTypeOptions,
      shelfTypeSearch,
      studioRateCardLoading,
      studioRateCardOptions,
      studioRateCardSearch,
      qualSampleLoading,
      qualSampleOptions,
      qualSampleSearch,
      researchLocLoading,
      researchLocOptions,
      researchLocSearch,
      updateLoading,
      statusLoading,
      statusOptions,
      statusSearch,
      countryForm,
      fieldLocations,
      checkStatus,
      localCsatResult,
      disableCountryChange,
      
      countryLoading,
      countrySearch,
      filteredCountries,

      onSubmit,
      resolveCountryStatusVariant,
      resolveCountryStatusLabelVariant,
      checkIfDeptSelected,
      
      // Field Validators
      validators: {
        required,
        requiredArray,
        integerValidator
      },

      icons: {
        mdiCodeTags,
        mdiPlus,
        mdiClose,
        mdiPencilOutline
      },
      stringToHslColor,
      avatarText

    }
  },
}
</script>
